import { isEqualWith, isNumber, map, parseInt } from 'lodash-es';
import bluebird from 'bluebird';
import { stringify } from 'qs/lib';
import URI from 'urijs';
import { getPageOrDefault } from '@is24at/react-pagination';
import accountApiClient from 'api/accountApiClient';
import prettyUrlsResultList from 'core/prettyUrlsResultList';
import transform from './resultListTransformer';

export { default as byIsLoading } from './selectors/isLoading';

export { default as byNoResults } from './selectors/noResults';

export { default as byTotalPages } from './selectors/totalPages';

export { default as byInitialPage } from './selectors/initialPage';

export { default as byInitialValues } from './selectors/initialValues';

export { default as byGacSpot } from './selectors/gacSpot';

// ------------------------------------
// Constants
// ------------------------------------
const EMPTY_RESULT_LIST = {
  totalHits: 0,
  hits: [],
};

// ------------------------------------
// Action constants
// ------------------------------------
export const actionTypes = {
  FETCH_REQUESTED: 'FETCH_RESULT_LIST_REQUESTED',
  FETCH_SUCCEEDED: 'FETCH_RESULT_LIST_SUCCEEDED',
  FETCH_FAILED: 'FETCH_RESULT_LIST_FAILED',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
};

// ------------------------------------
// Actions Creators
// ------------------------------------

/**
 * The page key from the {@code searchQuery} is
 * used as the initialPage in the Pagination component
 */
export const fetchRealtorsRequest = (searchQuery: any) => ({
  type: actionTypes.FETCH_REQUESTED,
  searchQuery,
});

export const fetchRealtorsSuccess = (response: any) => ({
  type: actionTypes.FETCH_SUCCEEDED,

  ...response,
});

export const fetchRealtorsFailure = () => ({
  type: actionTypes.FETCH_FAILED,
});

export const setSearchQuery = (searchQuery: any) => ({
  type: actionTypes.SET_SEARCH_QUERY,
  searchQuery,
});

// ---
// Side effects
// ---
export const reloadByUpdatingSort =
  ({ location, push }: any) =>
  (sort: any) => {
    const query = new URI(location.search);
    return push({
      ...location,
      search: query.setQuery({ sort }).toString(),
    });
  };

export const pushPageParameterBy = ({ location, push }: any, page: any) => {
  const query = new URI(location.search);
  return push({
    ...location,
    search: query.setQuery('page', getPageOrDefault({ page })).toString(),
  });
};

export const reloadRouteByPushingUrl = (
  filterValues: any,
  { location, push }: any,
) =>
  prettyUrlsResultList
    .buildLocationFromFilters(filterValues, location)
    .tap(({ pathname }: any) => push(`${pathname}?${stringify(filterValues)}`));

export const queryEquals = (query1: any, query2: any) =>
  isEqualWith(query1, query2, (value: any, other: any) =>
    !isNumber(value) && !isNumber(other)
      ? undefined
      : parseInt(value) === parseInt(other),
  );

/**
 * Fetches a list of realtors matching the given query.
 */
export const fetchRealtors = (query: any, state: any) => (dispatch: any) =>
  bluebird
    .try(() => dispatch(fetchRealtorsRequest(query)))
    .then(() => accountApiClient.fetchRealtors(query))
    .then((result: any) => {
      const transformedResultList = map(result.hits, (hit: any) =>
        transform(hit, state),
      );
      return dispatch(
        fetchRealtorsSuccess({ ...result, hits: transformedResultList }),
      );
    })
    .tapCatch(() => dispatch(fetchRealtorsFailure()));

// ------------------------------------
// Reducer
// ------------------------------------
const INITIAL_STATE = { ...EMPTY_RESULT_LIST, searchQuery: undefined };

export const reducer = (state = INITIAL_STATE, action: any) => {
  switch (action.type) {
    case actionTypes.FETCH_REQUESTED:
      return {
        ...state,
        isLoading: true,
        searchQuery: action.searchQuery,
      };
    case actionTypes.FETCH_SUCCEEDED:
      return {
        hits: action.hits,
        totalHits: action.totalHits,
        totalPages: action.totalPages,
        searchQuery: state.searchQuery,
        isLoading: false,
      };
    case actionTypes.FETCH_FAILED:
      return {
        ...EMPTY_RESULT_LIST,
        isLoading: false,
      };
    case actionTypes.SET_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.searchQuery,
      };
    default:
      return state;
  }
};
